import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Mail, Plus, Inbox, Star, Trash2, CheckSquare, RefreshCw, Search,
  CircleUser, Info, Loader2, Send,
} from 'lucide-react';
import { toast } from 'sonner';

const PROVIDERS = [
  { id: 'gmail', name: 'Gmail' },
  { id: 'outlook', name: 'Outlook' },
  { id: 'imap', name: 'IMAP / Otro' },
];

const PROVIDER_COLOR = { gmail: '#E35D6A', outlook: '#4C86C9', imap: '#C9A84C' };

const timeAgo = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    return `${Math.floor(diff / 86400)} d`;
  } catch { return ''; }
};

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export default function EmailsPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [activeAcc, setActiveAcc] = useState('all');
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [onlyUnread, setOnlyUnread] = useState(false);

  // Conectar cuenta
  const [connectOpen, setConnectOpen] = useState(false);
  const [form, setForm] = useState({ address: '', provider: 'gmail', label: '' });
  const [connecting, setConnecting] = useState(false);

  // Crear tarea
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ priority: 'medium', due_date: '' });
  const [creatingTask, setCreatingTask] = useState(false);

  const inputCls = 'w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489] text-foreground';

  const loadAccounts = async () => {
    try { const { data } = await api.get('/emails/accounts'); setAccounts(data); return data; }
    catch { return []; }
  };
  const loadMessages = async (accId) => {
    setLoadingMsgs(true);
    try {
      const qs = accId && accId !== 'all' ? `?email_account_id=${accId}` : '';
      const { data } = await api.get(`/emails/messages${qs}`);
      setMessages(data);
    } catch { setMessages([]); }
    setLoadingMsgs(false);
  };

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { loadMessages(activeAcc); }, [activeAcc]);

  const connect = async () => {
    if (!form.address.includes('@')) return toast.error('Introduce una dirección de correo válida');
    setConnecting(true);
    try {
      await api.post('/emails/accounts', form);
      toast.success('Cuenta conectada. Cargamos tus correos de demostración.');
      setConnectOpen(false);
      setForm({ address: '', provider: 'gmail', label: '' });
      await loadAccounts();
      loadMessages(activeAcc);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo conectar la cuenta'); }
    setConnecting(false);
  };

  const disconnect = async (id) => {
    try {
      await api.delete(`/emails/accounts/${id}`);
      toast.success('Cuenta desconectada');
      if (activeAcc === id) setActiveAcc('all');
      await loadAccounts();
      loadMessages(activeAcc === id ? 'all' : activeAcc);
    } catch { toast.error('No se pudo desconectar'); }
  };

  const openMessage = async (m) => {
    setDetailLoading(true);
    setSelected(m);
    try {
      const { data } = await api.get(`/emails/messages/${m.id}`);
      setSelected(data);
      if (m.unread) {
        setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, unread: false } : x));
        setAccounts((prev) => prev.map((a) => a.id === m.email_account_id ? { ...a, unread_count: Math.max(0, (a.unread_count || 0) - 1) } : a));
      }
    } catch { toast.error('No se pudo abrir el correo'); }
    setDetailLoading(false);
  };

  const toggleStar = async (m, e) => {
    e?.stopPropagation();
    try {
      const { data } = await api.post(`/emails/messages/${m.id}/star`);
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, starred: data.starred } : x));
      if (selected?.id === m.id) setSelected({ ...selected, starred: data.starred });
    } catch (err) { console.error('No se pudo marcar el correo como destacado:', err); }
  };

  const createTask = async () => {
    if (!selected) return;
    setCreatingTask(true);
    try {
      await api.post(`/emails/messages/${selected.id}/create-task`, {
        priority: taskForm.priority,
        due_date: taskForm.due_date || null,
      });
      toast.success('Tarea creada desde el correo. Míralo en Tareas & Proyectos.');
      setTaskOpen(false);
      setTaskForm({ priority: 'medium', due_date: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo crear la tarea'); }
    setCreatingTask(false);
  };

  const filtered = useMemo(() => {
    let list = messages;
    if (onlyUnread) list = list.filter((m) => m.unread);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((m) => (m.subject || '').toLowerCase().includes(q) || (m.from_name || '').toLowerCase().includes(q) || (m.snippet || '').toLowerCase().includes(q));
    return list;
  }, [messages, query, onlyUnread]);

  const totalUnread = accounts.reduce((a, x) => a + (x.unread_count || 0), 0);

  return (
    <div data-testid="emails-module">
      <PageHeader
        kicker="Comunicaciones"
        title="Bandeja de correos"
        subtitle="Conecta tus cuentas de correo y gestiona todo desde un solo lugar. Convierte cualquier mensaje en una tarea."
        actions={
          <button data-testid="connect-email-btn" onClick={() => setConnectOpen(true)}
            className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}>
            <Plus size={16} /> Conectar cuenta
          </button>
        }
      />

      {/* Aviso modo demostración */}
      <div className="pp-glass p-3 mb-4 flex items-start gap-2 text-xs text-muted-foreground" data-testid="emails-demo-notice">
        <Info size={15} className="pp-mint shrink-0 mt-0.5" />
        <span>Modo demostración: los correos son de ejemplo para que veas el flujo. La conexión real con Gmail y Outlook (OAuth) se habilita tras el despliegue.</span>
      </div>

      <div className="grid lg:grid-cols-[260px_360px_1fr] gap-4">
        {/* Columna 1: cuentas / carpetas */}
        <div className="space-y-2" data-testid="email-accounts-panel">
          <button data-testid="folder-all" onClick={() => { setActiveAcc('all'); setSelected(null); }}
            className={`w-full text-left pp-glass p-3 flex items-center gap-2 transition-shadow ${activeAcc === 'all' ? 'pp-glow-mint' : ''}`}>
            <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(62,180,137,0.14)' }}><Inbox size={16} className="pp-mint" /></span>
            <span className="flex-1 text-sm font-medium">Todos los correos</span>
            {totalUnread > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-[#07090A]" style={{ background: '#3EB489' }}>{totalUnread}</span>}
          </button>

          <div className="text-[11px] uppercase tracking-wide text-muted-foreground px-2 pt-2">Cuentas conectadas</div>
          {accounts.length === 0 && (
            <div className="pp-glass p-4 text-center text-xs text-muted-foreground" data-testid="no-accounts">
              Aún no has conectado ninguna cuenta. Pulsa “Conectar cuenta”.
            </div>
          )}
          {accounts.map((a) => (
            <div key={a.id} data-testid={`email-account-${a.id}`}
              className={`w-full pp-glass p-3 flex items-center gap-2 group transition-shadow cursor-pointer ${activeAcc === a.id ? 'pp-glow-mint' : ''}`}
              onClick={() => { setActiveAcc(a.id); setSelected(null); }}>
              <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: (PROVIDER_COLOR[a.provider] || '#3EB489') + '22' }}>
                <Mail size={15} style={{ color: PROVIDER_COLOR[a.provider] || '#3EB489' }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.label}</div>
                <div className="text-[11px] text-muted-foreground truncate">{a.address}</div>
              </div>
              {a.unread_count > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full pp-mint bg-[rgba(62,180,137,0.14)]">{a.unread_count}</span>}
              <button data-testid={`disconnect-${a.id}`} onClick={(e) => { e.stopPropagation(); disconnect(a.id); }}
                className="opacity-0 group-hover:opacity-70 hover:opacity-100 text-red-400 shrink-0"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>

        {/* Columna 2: lista de mensajes */}
        <div className="space-y-3" data-testid="email-list-panel">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-xl bg-[rgba(255,255,255,0.06)] border">
              <Search size={15} className="text-muted-foreground shrink-0" />
              <input data-testid="email-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar correos..." className="flex-1 bg-transparent outline-none text-sm min-w-0" />
            </div>
            <button data-testid="toggle-unread" onClick={() => setOnlyUnread((v) => !v)} title="Solo no leídos"
              className={`w-10 h-10 rounded-xl grid place-items-center border shrink-0 ${onlyUnread ? 'pp-mint border-[#3EB489] bg-[rgba(62,180,137,0.14)]' : 'text-muted-foreground'}`}>
              <Mail size={16} />
            </button>
            <button data-testid="refresh-emails" onClick={() => loadMessages(activeAcc)} title="Actualizar" className="w-10 h-10 rounded-xl grid place-items-center border shrink-0 text-muted-foreground hover:text-foreground">
              <RefreshCw size={15} />
            </button>
          </div>

          <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pp-scroll pr-1" data-testid="email-messages">
            {loadingMsgs && Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="pp-glass p-3 space-y-2"><Skeleton className="h-3 w-1/3" /><Skeleton className="h-3 w-2/3" /><Skeleton className="h-3 w-full" /></div>
            ))}
            {!loadingMsgs && filtered.length === 0 && (
              <div className="pp-glass p-8 text-center text-sm text-muted-foreground" data-testid="empty-inbox">
                {accounts.length === 0 ? 'Conecta una cuenta para ver tus correos.' : 'No hay correos que coincidan.'}
              </div>
            )}
            {!loadingMsgs && filtered.map((m) => (
              <button key={m.id} data-testid={`email-item-${m.id}`} onClick={() => openMessage(m)}
                className={`w-full text-left pp-glass p-3 group transition-shadow ${selected?.id === m.id ? 'pp-glow-mint' : ''} ${m.unread ? 'border-l-2 border-l-[#3EB489]' : ''}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 text-[11px] font-bold ${m.unread ? 'text-[#07090A]' : 'pp-mint bg-[rgba(62,180,137,0.14)]'}`} style={m.unread ? { background: '#3EB489' } : {}}>
                    {initials(m.from_name) || <CircleUser size={15} />}
                  </span>
                  <span className={`flex-1 text-sm truncate ${m.unread ? 'font-bold' : 'font-medium'}`}>{m.from_name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(m.received_at)}</span>
                  <button onClick={(e) => toggleStar(m, e)} data-testid={`star-${m.id}`} className={`shrink-0 ${m.starred ? 'pp-gold' : 'text-muted-foreground opacity-0 group-hover:opacity-70'}`}>
                    <Star size={14} fill={m.starred ? '#C9A84C' : 'none'} />
                  </button>
                </div>
                <div className={`text-sm mt-1 truncate ${m.unread ? 'font-semibold' : 'text-muted-foreground'}`}>{m.subject}</div>
                <div className="text-[12px] text-muted-foreground truncate mt-0.5">{m.snippet}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Columna 3: detalle */}
        <div className="pp-glass p-5 min-h-[420px]" data-testid="email-detail-panel">
          {!selected ? (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-16">
              <div>
                <Mail size={44} className="pp-mint mx-auto mb-3 opacity-60" />
                <div className="text-sm">Selecciona un correo para leerlo aquí.</div>
              </div>
            </div>
          ) : detailLoading ? (
            <div className="space-y-3"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-4 w-1/3" /><Skeleton className="h-40 w-full" /></div>
          ) : (
            <div className="space-y-4" data-testid="email-detail">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold leading-snug" data-testid="detail-subject">{selected.subject}</h2>
                  <div className="text-sm text-muted-foreground mt-1 truncate">
                    <span className="font-medium text-foreground">{selected.from_name}</span> · {selected.from_addr}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Para: {selected.to_addr} · {new Date(selected.received_at).toLocaleString('es')}</div>
                </div>
                <button onClick={(e) => toggleStar(selected, e)} className={`shrink-0 ${selected.starred ? 'pp-gold' : 'text-muted-foreground'}`}>
                  <Star size={18} fill={selected.starred ? '#C9A84C' : 'none'} />
                </button>
              </div>

              <div className="border-t border-[rgba(255,255,255,0.08)] pt-4 text-sm whitespace-pre-line leading-relaxed" data-testid="detail-body">
                {selected.body}
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[rgba(255,255,255,0.08)]">
                <button data-testid="create-task-from-email" onClick={() => setTaskOpen(true)}
                  className="h-10 px-4 rounded-xl text-sm font-semibold text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}>
                  <CheckSquare size={16} /> Crear tarea
                </button>
                <a href={`mailto:${selected.from_addr}?subject=RE: ${encodeURIComponent(selected.subject || '')}`}
                  className="h-10 px-4 rounded-xl text-sm border flex items-center gap-2 pp-mint" data-testid="reply-email">
                  <Send size={15} /> Responder
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog: conectar cuenta */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="pp-glass-strong" data-testid="connect-dialog">
          <DialogHeader><DialogTitle>Conectar cuenta de correo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Proveedor</label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger data-testid="provider-select" className="mt-1 h-11 pp-glass"><SelectValue /></SelectTrigger>
                <SelectContent className="pp-glass-strong">{PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Dirección de correo</label>
              <input data-testid="email-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="tucorreo@dominio.com" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Etiqueta (opcional)</label>
              <input data-testid="email-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ej. Cuenta empresa" className={`${inputCls} mt-1`} />
            </div>
            <div className="text-[11px] text-muted-foreground flex gap-2 p-2 rounded-lg bg-[rgba(62,180,137,0.06)]">
              <Info size={13} className="pp-mint shrink-0 mt-0.5" /> Se cargarán correos de demostración. La autenticación OAuth real se habilita post-despliegue.
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-connect" onClick={connect} disabled={connecting}
              className="w-full h-11 rounded-xl font-semibold text-[#07090A] flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: '#3EB489' }}>
              {connecting ? <><Loader2 size={16} className="animate-spin" /> Conectando...</> : <><Plus size={16} /> Conectar</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: crear tarea desde correo */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="pp-glass-strong" data-testid="task-from-email-dialog">
          <DialogHeader><DialogTitle>Crear tarea desde el correo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm p-3 rounded-xl bg-[rgba(255,255,255,0.05)] border">
              <div className="text-[11px] text-muted-foreground">Asunto</div>
              <div className="font-medium truncate">{selected?.subject}</div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Prioridad</label>
              <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                <SelectTrigger data-testid="task-priority-select" className="mt-1 h-11 pp-glass"><SelectValue /></SelectTrigger>
                <SelectContent className="pp-glass-strong">
                  <SelectItem value="low">Baja</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Fecha límite (opcional)</label>
              <input data-testid="task-due-input" type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className={`${inputCls} mt-1`} />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-create-task" onClick={createTask} disabled={creatingTask}
              className="w-full h-11 rounded-xl font-semibold text-[#07090A] flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: '#3EB489' }}>
              {creatingTask ? <><Loader2 size={16} className="animate-spin" /> Creando...</> : <><CheckSquare size={16} /> Crear tarea</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
